import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  ViewEncapsulation
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { MaskitoElementPredicate, MaskitoOptions } from '@maskito/core';
import { Platform } from '@ionic/angular';
import {
  animate,
  state,
  style,
  transition,
  trigger
} from '@angular/animations';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-base-input',
  templateUrl: './base-input.component.html',
  styleUrls: ['./base-input.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => BaseInputComponent),
      multi: true
    }
  ],
  animations: [
    trigger('fadeIn', [
      state('void', style({ opacity: 0 })),
      state('*', style({ opacity: 1 })),
      transition('void => *', animate('500ms ease-in'))
    ])
  ]
})
export class BaseInputComponent implements OnInit, OnChanges, OnDestroy {
  @Input() title?: string = '';
  @Input() placeholder?: string = '';
  @Input() type?: string = '';
  @Input() boldInput?: boolean;
  @Input() roundCorner?: boolean;
  @Input() control: any;
  @Input() labelColor: string = '#242734';
  /** Поле только для чтения (напр. ФИО, автозаполняемое по ИНН из гос. реестра). */
  @Input() readonly: boolean = false;

  customErrorsFieldTypes = ['email', 'inn', 'password', 'secretKey', 'sdiz'];
  showPassword: boolean = false;
  inputValue: string;
  isAndroid: boolean;
  showDatePicker: boolean;
  datePickerValue: string;
  isValid?: boolean = false;
  isInvalid?: boolean = false;
  public isDisabled: boolean = false;
  currentDate: string = new Date().toISOString();

  readonly carNumberMask: MaskitoOptions = {
    mask: [
      /[a-zA-Za-яA-Я]/,
      /\d/,
      /\d/,
      /\d/,
      /[a-zA-Za-яA-Я]/,
      /[a-zA-Za-яA-Я]/,
      /\d/,
      /\d/,
      /\d?/
    ]
  };

  readonly trailerNumberMask: MaskitoOptions = {
    mask: [
      /[a-zA-Za-яA-Я]/,
      /[a-zA-Za-яA-Я]/,
      /\d/,
      /\d/,
      /\d/,
      /\d/,
      /\d/,
      /\d/,
      /\d?/
    ]
  };

  readonly dateMask: MaskitoOptions = {
    mask: [/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/]
  };

  readonly phoneMask: MaskitoOptions = {
    mask: [
      '+',
      '7',
      ' ',
      '(',
      /\d/,
      /\d/,
      /\d/,
      ')',
      ' ',
      /\d/,
      /\d/,
      /\d/,
      '-',
      /\d/,
      /\d/,
      '-',
      /\d/,
      /\d/
    ]
  };

  private readonly EmailPattern: RegExp =
    /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  private onChange: any;
  private onTouched: any;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private platform: Platform,
    private cdr: ChangeDetectorRef
  ) {}

  get isInvalidTaxNumber(): boolean {
    return (
      this.isInvalid &&
      this.type === 'inn' &&
      (this.control.errors.invalidTaxNumber ||
        this.control.errors.minlength ||
        this.control.errors.maxlength)
    );
  }

  readonly maskPredicate: MaskitoElementPredicate = async el =>
    (el as HTMLIonInputElement).getInputElement();

  ngOnInit() {
    this.isAndroid = this.platform.is('android');
  }

  ngOnChanges(): void {
    if (this.control) {
      this.isValid = this.control.valid;
      this.isInvalid = this.control.invalid && this.control.touched;

      this.control.statusChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.isValid = this.control.valid;
          this.isInvalid = this.control.invalid && this.control.touched;
          this.cdr.markForCheck();
        });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleShowPassword() {
    this.showPassword = !this.showPassword;
    this.cdr.markForCheck();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  writeValue(value: any): void {
    if (this.type === 'calendar' && !!value) {
      if (value) {
        this.datePickerValue = new Date(value).toISOString();
        const dateObj = new Date(Number(value));
        const isoDateString = dateObj.toISOString();
        this.datePickerValue = isoDateString;
      } else {
        return;
      }
    }

    this.inputValue = value;
    this.cdr.markForCheck();
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    this.cdr.markForCheck();
  }

  updateValue() {
    if (this.type === 'carNumber' || this.type === 'trailerNumber') {
      this.inputValue = this.inputValue.toUpperCase();
    }

    this.onChange(this.inputValue);
    this.onTouched();
    this.validate();
    this.cdr.markForCheck();
  }

  weightInputValueChanged() {
    const value = this.inputValue ?? '';

    if (!value || /^0+$/.test(value)) {
      this.onChange(value);
      this.onTouched();
      this.validate();
      this.cdr.markForCheck();
      return;
    }

    const numericValue = parseInt(value, 10);

    if (Number.isNaN(numericValue) || numericValue <= 0) {
      this.inputValue = '1';
    }

    this.onChange(this.inputValue);
    this.onTouched();
    this.validate();
    this.cdr.markForCheck();
  }

  normalizeWeightOnBlur() {
    this.onTouched();

    if (!this.inputValue) {
      this.cdr.markForCheck();
      return;
    }

    const numericValue = parseInt(this.inputValue, 10);

    if (Number.isNaN(numericValue) || numericValue <= 0) {
      this.inputValue = '1';
      this.onChange(this.inputValue);
      this.validate();
      this.cdr.markForCheck();
      return;
    }

    this.cdr.markForCheck();
  }

  openDatePicker() {
    if (!this.isDisabled) {
      if (this.showDatePicker) this.showDatePicker = false;
      else this.showDatePicker = true;
    }
  }

  setDate() {
    if (this.datePickerValue) {
      this.inputValue = Date.parse(this.datePickerValue).toString();
      this.onChange(this.inputValue);
      this.showDatePicker = false;
      this.validate();
    }
  }

  clearDatePick() {
    this.inputValue = '';
    this.onChange(this.inputValue);
    this.datePickerValue = '';
    this.showDatePicker = false;
  }

  validate() {
    if (this.inputValue) {
      switch (this.type) {
        case 'carNumber':
          if (this.inputValue.length < 7) {
            this.control.setErrors({ incorrect: true });
          }
          break;
        case 'trailerNumber':
          if (this.inputValue.length < 8) {
            this.control.setErrors({ incorrect: true });
          }
          break;
        case 'phone':
          if (this.inputValue.length !== 18) {
            this.control.setErrors({ incorrect: true });
          }
          break;
        case 'email': {
          const isValidEmail = this.EmailPattern.test(this.inputValue);
          if (!isValidEmail) {
            this.control.setErrors({ pattern: true });
          }
          break;
        }
        default:
          if (this.control) {
            this.control.updateValueAndValidity({ onlySelf: true });
          }
          break;
      }
    }
  }

  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const clipboardData = event.clipboardData?.getData('text') || '';
    let sanitizedValue = clipboardData.trim();

    if (this.type === 'phone') {
      const clipboardData = event.clipboardData?.getData('text') || '';
      const digits = clipboardData.replace(/\D/g, '');

      let normalizedPhone = digits;
      if (digits.startsWith('8')) {
        normalizedPhone = '7' + digits.slice(1);
      }

      if (normalizedPhone.length > 11) {
        normalizedPhone = normalizedPhone.slice(0, 11);
      }

      sanitizedValue =
        `+7 (${normalizedPhone.slice(1, 4)}) ${normalizedPhone.slice(4, 7)}` +
        `-${normalizedPhone.slice(7, 9)}-${normalizedPhone.slice(9, 11)}`;
    }

    this.inputValue = sanitizedValue;
    this.control.setValue(sanitizedValue, { emitEvent: true });
    this.onChange(sanitizedValue);
    this.onTouched();
    this.validate();
    this.cdr.markForCheck();
  }
}
